export function deepEqual(obj1: any, obj2: any): boolean {
    let differences: string[] = [];

    function compare(o1: any, o2: any, path: string): void {
        // If both values are strictly equal, nothing to log.
        if (o1 === o2) {
            return;
        }

        // If either is null or not an object, log the difference.
        if (
            typeof o1 !== 'object' || o1 === null ||
            typeof o2 !== 'object' || o2 === null
        ) {
            differences.push(`Difference at ${path || 'root'}: object1 = ${o1}, object2 = ${o2}`);
            return;
        }

        // Get keys from both objects.
        const keys1 = Object.keys(o1);
        const keys2 = Object.keys(o2);

        // Check for different number of keys.
        if (keys1.length !== keys2.length) {
            differences.push(`Difference at ${path || 'root'}: different number of keys (object1 has ${keys1.length}, object2 has ${keys2.length})`);
        }

        // Compare each key in object1.
        for (const key of keys1) {
            const newPath = path ? `${path}.${key}` : key;
            if (!o2.hasOwnProperty(key)) {
                differences.push(`Difference at ${newPath}: key exists in object1 but is missing in object2`);
            } else {
                compare(o1[key], o2[key], newPath);
            }
        }

        // Also check keys in object2 not present in object1.
        for (const key of keys2) {
            const newPath = path ? `${path}.${key}` : key;
            if (!o1.hasOwnProperty(key)) {
                differences.push(`Difference at ${newPath}: key exists in object2 but is missing in object1`);
            }
        }
    }

    // Start comparison from the root.
    compare(obj1, obj2, '');

    // Log differences if any, then return the boolean result.
    if (differences.length > 0) {
        differences.forEach(diff => console.log(diff));
        return false;
    }

    return true;
}

/**
 * Swaps the 'order' value between two objects at indexA and indexB,
 * then sorts the array by the 'order' property in ascending order.
 *
 * @param arr The array containing objects with an 'order' property.
 * @param indexA The index of the first object.
 * @param indexB The index of the second object.
 * @returns The sorted array.
 */
export function swapOrderAndSort<T extends { order: number }>(
    arr: T[],
    indexA: number,
    indexB: number
): T[] {
    if (indexA >= arr.length || indexB >= arr.length) {
        console.error("Invalid indices provided");
        return arr;
    }
    arr[indexA].order = indexB + 1;
    arr[indexB].order = indexA + 1;

    // Sort the array by the 'order' property (ascending order)
    return arr.sort((a, b) => a.order - b.order);
}

export function moveItemByOrder(
    arr: any[],
    fromOrder: number,
    toOrder: number,
    orderKey: string = 'order'
): any[] {
    // Find the item with the specified order value.
    const itemIndex = arr.findIndex(item => item[orderKey] === fromOrder);
    if (itemIndex === -1) {
        console.error(`No item found with ${orderKey} = ${fromOrder}`);
        return arr;
    }

    // Remove the item from the array.
    const [movedItem] = arr.splice(itemIndex, 1);

    // Adjust order values depending on move direction.
    if (fromOrder < toOrder) {
        // For items with an order value between fromOrder and toOrder (inclusive),
        // decrement their order by 1.
        arr.forEach(item => {
            if (item[orderKey] > fromOrder && item[orderKey] <= toOrder) {
                item[orderKey] -= 1;
            }
        });
    } else if (fromOrder > toOrder) {
        // For items with an order value between toOrder and fromOrder (exclusive),
        // increment their order by 1.
        arr.forEach(item => {
            if (item[orderKey] >= toOrder && item[orderKey] < fromOrder) {
                item[orderKey] += 1;
            }
        });
    }

    // Assign the new order value to the moved item.
    movedItem[orderKey] = toOrder;

    // Push the moved item back into the array.
    arr.push(movedItem);

    // Sort the array by the provided key.
    arr.sort((a, b) => a[orderKey] - b[orderKey]);

    return arr;
}

export function logDifferentKeys(arr: any[]): void {
    if (arr.length === 0) {
        console.log("No objects provided.");
        return;
    }

    // Build a union of all keys except for the ignored key (ds_layout)
    const unionKeys = new Set<string>();
    arr.forEach((obj) => {
        Object.keys(obj)
            .filter(key => key !== "ds_layout")
            .forEach((key) => unionKeys.add(key));
    });

    // For each key in the union, find which objects are missing it.
    unionKeys.forEach((key) => {
        const missingIndices: number[] = [];
        arr.forEach((obj, index) => {
            if (!obj.hasOwnProperty(key)) {
                missingIndices.push(index);
            }
        });
        if (missingIndices.length > 0) {
            console.log(`Key "${key}" is missing in objects at indices: ${missingIndices.join(", ")}`);
        }
    });
}

export function addMissingKeys(arr: any[], orderKey: string = 'order'): any[] {
    // Build union of all keys, ignoring "ds_layout"
    const unionKeys = new Set<string>();
    arr.forEach(obj => {
        Object.keys(obj)
            .filter(key => key !== 'ds_layout')
            .forEach(key => unionKeys.add(key));
    });

    // For each object in the array, add the missing keys with a suffix of the object's order value
    arr.forEach(obj => {
        const orderVal = obj[orderKey];
        // Only process if orderVal is defined or not null
        if (orderVal !== undefined && orderVal !== null) {
            unionKeys.forEach(key => {
                if (!(key in obj)) {
                    // Create the new key (e.g. "surpluskey" becomes "surpluskey5" if order = 5)
                    const newKey = `${key}${orderVal}`;
                    obj[newKey] = ''; // Set a default value; adjust as needed
                }
            });
        }
    });

    return arr;
}

export function findOtherKeyValue(
    arr: any[],
    searchValue: any,
    targetKey: string
): any {
    for (const obj of arr) {
        // Look for any property in the object that equals searchValue.
        for (const key in obj) {
            if (obj.hasOwnProperty(key) && obj[key] === searchValue) {
                // Once found, return the value of the specified target key.
                return obj[targetKey];
            }
        }
    }
    return undefined; // Return undefined if no match is found.
}
